import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = ["onMousedown"]
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = ["onMousedown"]
const _hoisted_5 = ["innerHTML"]

import { ref, computed, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useVirtualizer } from '@tanstack/vue-virtual';
import sanitizeHtml from 'sanitize-html';
import ClickOutside from '@makeabledk/vue-ui/directives/click-outside';
import SearchInput from '@/components/ui/SearchInput.vue';

const LARGE_LIST_THRESHOLD = 200;
const ITEM_HEIGHT = 44;


export default /*@__PURE__*/_defineComponent({
  __name: 'AutoComplete',
  props: {
    label: {},
    items: { default: () => [] },
    itemText: {},
    itemValue: {},
    dropdownHeightClass: { default: 'max-h-64' },
    search: { default: null }
  },
  emits: ["update:value", "update:search", "item-clicked"],
  setup(__props: any, { emit: __emit }) {

const vClickOutside = ClickOutside;

const props = __props;

const emit = __emit;

const expandOptions = ref(false);
const localSearch = ref('');
const inputField = ref<InstanceType<typeof SearchInput> | null>(null);
const virtualListRef = ref<HTMLElement | null>(null);

const isLargeList = computed(() => props.items.length > LARGE_LIST_THRESHOLD);

const computedSearch = computed({
    get(): string {
        return props.search === null ? localSearch.value : props.search;
    },
    set(newValue: string) {
        if (props.search === null) {
            localSearch.value = newValue;
        } else {
            emit('update:search', newValue);
        }
    },
});

const debouncedSearchValue = refDebounced(
    computed(() => computedSearch.value),
    computed(() => (isLargeList.value ? 200 : 0))
);

const searchValueForFiltering = computed(() => (isLargeList.value ? debouncedSearchValue.value : computedSearch.value));

const searchRegex = computed(() => {
    const searchStr = searchValueForFiltering.value.trim();
    if (!searchStr) return null;
    const escaped = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(${escaped})`, 'gi');
});

const filteredItems = computed(() => {
    const searchString = searchValueForFiltering.value.trim().toLowerCase();
    if (!searchString) {
        return props.items;
    }
    return props.items.filter((item: any) => {
        const itemTextValue = item[props.itemText];
        if (typeof itemTextValue !== 'string') return false;
        return itemTextValue.trim().toLowerCase().includes(searchString);
    });
});

const virtualizer = useVirtualizer({
    get count() {
        return filteredItems.value.length;
    },
    getScrollElement: () => virtualListRef.value,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
});

watch(filteredItems, () => {
    if (virtualListRef.value) {
        virtualListRef.value.scrollTop = 0;
    }
});

const sanitizedTextCache = new Map<string, string>();

function getSanitizedText(text: string): string {
    if (sanitizedTextCache.has(text)) {
        return sanitizedTextCache.get(text)!;
    }
    const sanitized = sanitizeHtml(text);
    if (sanitizedTextCache.size > 1000) {
        const firstKey = sanitizedTextCache.keys().next().value;
        if (firstKey) sanitizedTextCache.delete(firstKey);
    }
    sanitizedTextCache.set(text, sanitized);
    return sanitized;
}

function getBoldenedString(text: string): string {
    const sanitized = getSanitizedText(text);
    const regex = searchRegex.value;
    if (!regex) return sanitized;
    return sanitized.replace(regex, '<b>$1</b>');
}

function onSearchSubmit() {
    if (filteredItems.value.length > 0) {
        emit('item-clicked', filteredItems.value[0]);
    }
}

function onEscape() {
    expandOptions.value = false;
    (document.activeElement as HTMLElement)?.blur();
}

function onItemClicked(item: any) {
    expandOptions.value = false;
    emit('item-clicked', item);
}

return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(SearchInput, {
      ref_key: "inputField",
      ref: inputField,
      value: computedSearch.value,
      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((computedSearch).value = $event)),
      label: _ctx.label,
      "prevent-esc-propagation": "",
      onSubmit: onSearchSubmit,
      onOnEscape: onEscape,
      onOnTab: _cache[1] || (_cache[1] = ($event: any) => (expandOptions.value = false)),
      onFocus: _cache[2] || (_cache[2] = ($event: any) => (expandOptions.value = true))
    }, null, 8, ["value", "label"]),
    _createVNode(_Transition, {
      "enter-active-class": "transition duration-200 ease-out",
      "enter-from-class": "transform scale-95 opacity-0",
      "enter-to-class": "transform scale-100 opacity-100",
      "leave-active-class": "transition duration-100 ease-out",
      "leave-from-class": "transform scale-100 opacity-100",
      "leave-to-class": "transform scale-95 opacity-0"
    }, {
      default: _withCtx(() => [
        (expandOptions.value && isLargeList.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "virtualListRef",
              ref: virtualListRef,
              class: _normalizeClass([[_ctx.dropdownHeightClass], "absolute top-full left-0 w-full bg-white border z-30 rounded overflow-y-auto"])
            }, [
              _createElementVNode("div", {
                style: _normalizeStyle({ height: `${_unref(virtualizer).getTotalSize()}px`, position: 'relative' })
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(virtualizer).getVirtualItems(), (virtualRow) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: filteredItems.value[virtualRow.index][_ctx.itemValue],
                    class: "hover:bg-gray-100 cursor-pointer absolute left-0 w-full box-border",
                    style: _normalizeStyle({
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                        }),
                    onMousedown: ($event: any) => (onItemClicked(filteredItems.value[virtualRow.index]))
                  }, [
                    _createElementVNode("span", {
                      class: "p-3 w-full block truncate",
                      innerHTML: getBoldenedString(filteredItems.value[virtualRow.index][_ctx.itemText])
                    }, null, 8, _hoisted_3)
                  ], 44, _hoisted_2))
                }), 128))
              ], 4)
            ], 2))
          : (expandOptions.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: _normalizeClass([[_ctx.dropdownHeightClass], "absolute top-full left-0 w-full bg-white border z-30 rounded overflow-y-auto"])
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredItems.value, (item) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: item[_ctx.itemValue],
                    class: "hover:bg-gray-100 cursor-pointer",
                    onMousedown: ($event: any) => (onItemClicked(item))
                  }, [
                    _createElementVNode("span", {
                      class: "p-3 w-full block truncate",
                      innerHTML: getBoldenedString(item[_ctx.itemText])
                    }, null, 8, _hoisted_5)
                  ], 40, _hoisted_4))
                }), 128))
              ], 2))
            : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ])), [
    [_unref(vClickOutside), () => (expandOptions.value = false)]
  ])
}
}

})